/**
 * Moving between the two things one account can be.
 *
 * Somebody who owns the company and also works at it has an administrative
 * workspace and an employee experience. They are different places with
 * different jobs, and the person needs to be able to say which one they are in
 * without it being decided for them.
 *
 * RULES THIS ENCODES
 *   · The default landing area does not change. An owner still lands in the
 *     admin workspace; this is a door, not a redirect.
 *   · "My employee view" appears only when there is an active employee record
 *     to view — offering a door to an empty room is worse than no door.
 *   · The way back is always present in the portal, for anyone who has an
 *     administrative area to go back to.
 *   · A portal-only account sees neither control, because for them there is
 *     only one place and a switch implies otherwise.
 *
 * The switch grants nothing. It is navigation; the route gate and the database
 * decide what is actually permitted once you arrive.
 */

import { useNavigate } from 'react-router-dom';
import { Briefcase, UserRound } from 'lucide-react';
import { useProfile } from '../contexts/ProfileContext';
import { useEmployeeSelf } from '../hooks/useEmployeeSelf';

/** The name an internal area goes by, for the person looking at it. */
function internalWorkspaceLabel(role: string | null | undefined): string | null {
  if (role === 'owner' || role === 'admin') return 'Admin workspace';
  if (role === 'member') return 'Team workspace';
  return null;
}

function internalWorkspacePath(role: string | null | undefined): string | null {
  if (role === 'owner' || role === 'admin') return '/dashboard';
  if (role === 'member') return '/workspace';
  return null;
}

/**
 * Shown in the ADMIN shell: "My employee view".
 *
 * Queries for an employee record only where one could plausibly exist — an
 * account with no internal role is never in the admin shell to begin with.
 */
export function MyEmployeeViewLink({ collapsed = false }: { collapsed?: boolean }) {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const isInternal = profile?.role === 'owner' || profile?.role === 'admin' || profile?.role === 'member';
  const { state } = useEmployeeSelf(isInternal);

  if (!isInternal || state !== 'active') return null;

  return (
    <button
      type="button"
      onClick={() => navigate('/portal')}
      // Unconditional: the label disappeared with the text when the sidebar
      // collapsed, and `title` is not a reliable accessible name.
      aria-label="My employee view"
      title={collapsed ? 'My employee view' : undefined}
      className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-panel hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-[#4C6FFF]/30"
      style={collapsed ? { justifyContent: 'center', paddingLeft: 0, paddingRight: 0 } : undefined}
    >
      <UserRound className="w-4 h-4 shrink-0" aria-hidden />
      {!collapsed && <span className="truncate">My employee view</span>}
    </button>
  );
}

/**
 * Shown in the PORTAL: the way back to administration.
 *
 * Absent for a portal-only account, who has nowhere to go back to — an
 * inert control that reads as a locked door is worse than no control.
 */
export function BackToWorkspaceLink({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const label = internalWorkspaceLabel(profile?.role);
  const path = internalWorkspacePath(profile?.role);

  if (!label || !path) return null;

  return (
    <button
      type="button"
      onClick={() => { onNavigate?.(); navigate(path); }}
      className="w-full px-4 py-2 text-left text-sm text-foreground dark:text-[#E2E8F0] hover:bg-background dark:hover:bg-[#1E293B] transition-colors flex items-center gap-2.5"
      role="menuitem"
    >
      <Briefcase className="w-4 h-4 shrink-0" aria-hidden />
      {label}
    </button>
  );
}
